import { morph } from '@theme/morph';
import { getViewParameterValue } from '@theme/utilities';

/**
 * Swaps between version products in place.
 *
 * The three versions are separate products, so picking one is a product change rather than a
 * variant change. Rather than navigating, this does what the theme already does for combined
 * listings in variant-picker.js: fetch the target product with `section_id`, morph <main>, and
 * replaceState the URL. Same Section Rendering API, same morph helper, so the gallery, price,
 * buy buttons and the cards themselves all come back consistent.
 *
 * Progressive enhancement: the cards are real anchors, so with JS unavailable or this module
 * failing to load they still navigate to the product. Nothing here is required for correctness.
 */
class VpVersionCards extends HTMLElement {
  #abort;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.#openRequestedVersion();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#abort?.abort();
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    // Let modified clicks (new tab, download, middle-click) behave natively.
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (event.button !== 0) return;

    const card = /** @type {HTMLAnchorElement | null} */ (
      /** @type {Element} */ (event.target).closest('a.vp-version-card')
    );
    if (!card || card.classList.contains('vp-version-card--selected')) return;

    const href = card.getAttribute('href');
    if (!href) return;

    event.preventDefault();
    this.#select(card);
    this.#swap(href, this.#matchVariant(card));
  };

  /**
   * Find the variant on the card's product that keeps the colours already chosen here.
   *
   * Each version is a separate product with its own variant ids, so the selection cannot be
   * carried by id. It is matched by option NAME and value instead, which also survives the two
   * products listing their options in a different order. Returns null when the target has no
   * equivalent (a version that does not come in the chosen colour), and the swap then falls
   * back to that product's default variant rather than guessing.
   */
  #matchVariant(card) {
    let names, variants;
    try {
      names = JSON.parse(card.dataset.optionNames || '[]');
      variants = JSON.parse(card.dataset.variants || '[]');
    } catch {
      return null;
    }
    if (!names.length || !variants.length) return null;

    // What is selected right now, keyed by option name. The name attribute is
    // "<OPTION>-<blockhash>-<id>", and an option name may itself contain a hyphen, so the
    // legend ("SEAT COLOR: Brown") is the reliable source and the attribute only a fallback.
    const chosen = new Map();
    // Scoped to the variant picker: other radio groups on the page (the media tab switcher,
    // for one) are not product options and must not be read as such.
    const picker = document.querySelector('variant-picker');
    for (const input of picker?.querySelectorAll('input[type="radio"]:checked') ?? []) {
      const legend = input.closest('fieldset')?.querySelector('legend')?.textContent ?? '';
      let name = legend.split(':')[0].trim();
      if (!name) {
        const attr = input.getAttribute('name') ?? '';
        name = attr.split('-')[0].trim();
      }
      if (name) chosen.set(name.toLowerCase(), input.value);
    }
    if (!chosen.size) return null;

    // Only the options both products share can be carried across.
    const shared = names
      .map((name, index) => ({ index, value: chosen.get(String(name).trim().toLowerCase()) }))
      .filter((entry) => entry.value != null);
    if (!shared.length) return null;

    const hit = variants.find((variant) => shared.every((entry) => variant.o?.[entry.index] === entry.value));
    return hit ? hit.id : null;
  }

  /** Optimistic selection so the click feels immediate; the morph confirms it.
   *  @param {Element} card */
  #select(card) {
    for (const el of this.querySelectorAll('.vp-version-card')) {
      el.classList.toggle('vp-version-card--selected', el === card);
      if (el === card) el.setAttribute('aria-current', 'true');
      else el.removeAttribute('aria-current');
    }
  }

  /** @param {string} href @param {number|null} [variantId] */

  /**
   * The cards only re-render their own section, but other sections describe the same product —
   * the specs table is entirely metafields — so they are left showing whatever the visitor
   * arrived on. Each key named in `data-swap-sections` is refreshed the same way.
   *
   * Keys, not rendered ids: a section's id embeds a number that belongs to the theme it was
   * rendered on, so an id copied from one theme resolves to nothing on another. Matching on the
   * key suffix finds the section wherever it lives.
   */
  #swapOthers(target, variantId, view) {
    const keys = (this.dataset.swapSections || '')
      .split(',')
      .map((k) => k.trim())
      .filter(Boolean);
    if (!keys.length) return;

    for (const key of keys) {
      const el =
        document.getElementById(`shopify-section-${key}`) ??
        document.querySelector(`[id^="shopify-section-"][id$="__${CSS.escape(key)}"]`);
      if (!el) {
        console.warn(`[vp-version-cards] no section on the page for "${key}"`);
        continue;
      }

      const sectionId = el.id.replace(/^shopify-section-/, '');
      const params = new URLSearchParams();
      params.set('section_id', sectionId);
      if (view) params.set('view', view);
      if (variantId) params.set('variant', String(variantId));

      fetch(`${target.pathname}?${params}`, { signal: this.#abort.signal })
        .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
        .then((text) => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const next = doc.getElementById(el.id) ?? doc.body.firstElementChild;
          // A section that fails to refresh keeps the copy it already has, which is wrong but
          // readable — better than morphing in an error page or emptying it.
          if (next) {
            morph(el, next);
            this.#reinit(el);
          }
        })
        .catch((error) => {
          if (error.name === 'AbortError') return;
          console.warn(`[vp-version-cards] could not refresh "${key}"`, error);
        });
    }
  }


  /**
   * A morph swaps in new child nodes, so every listener the section's own script bound is gone
   * with the elements it bound them to — the specs carousel's arrows and tabs stop responding.
   * Those scripts guard their setup behind a `data-…-init` flag and re-run it on
   * `shopify:section:load`, which is the signal the theme editor already sends when it replaces
   * a section. Clearing the flag and firing that event puts them back without this file needing
   * to know what any particular section does.
   */
  #reinit(el) {
    for (const key of Object.keys(el.dataset)) {
      if (/init$/i.test(key)) delete el.dataset[key];
    }
    for (const child of el.querySelectorAll('[data-section-id], [class*="__"]')) {
      for (const key of Object.keys(child.dataset)) {
        if (/init$/i.test(key)) delete child.dataset[key];
      }
    }
    const sectionId = el.id.replace(/^shopify-section-/, '');
    el.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true, detail: { sectionId } }));
  }

  /**
   * A page template shares the version's URL with nothing: its sections carry the page's own
   * ids, and a product URL knows only the product template's. So a page cannot use the
   * section-rendering route at all. It fetches the version's whole page on the template named
   * in `data-swap-view` and lifts every section it shares with that page out by key — the key
   * being the part of the id after `__`, which the page kept when it was built from the same
   * template. One request, however many sections.
   *
   * The page's own URL is kept, with `?version=` naming what is showing, so a link to it
   * reopens on the same version.
   */
  #swapFromPage(href, variantId) {
    const target = new URL(href, location.origin);
    const params = new URLSearchParams();
    params.set('view', this.dataset.swapView);
    if (variantId) params.set('variant', String(variantId));

    const keys = ['main']
      .concat((this.dataset.swapSections || '').split(','))
      .map((k) => k.trim())
      .filter(Boolean);

    // The version's page was rendered by the product template, whose cards do not carry these.
    // Morphing would strip them, and the next click would take the route that cannot work here.
    const carried = {
      swapView: this.dataset.swapView,
      swapSections: this.dataset.swapSections,
      currentHandle: target.pathname.split('/').filter(Boolean).pop() ?? '',
    };

    this.#abort?.abort();
    this.#abort = new AbortController();
    this.setAttribute('aria-busy', 'true');

    fetch(`${target.pathname}?${params}`, { signal: this.#abort.signal })
      .then((response) => (response.ok ? response.text() : Promise.reject(new Error(String(response.status)))))
      .then((text) => {
        const incoming = new DOMParser().parseFromString(text, 'text/html');
        let swapped = 0;
        for (const key of keys) {
          const here = document.querySelector(`[id^="shopify-section-"][id$="__${CSS.escape(key)}"]`);
          const there = incoming.querySelector(`[id^="shopify-section-"][id$="__${CSS.escape(key)}"]`);
          if (!here || !there) {
            console.warn(`[vp-version-cards] "${key}" is not on both pages`);
            continue;
          }
          // The incoming section carries the product template's id; the page keeps its own, so
          // the same key resolves on the next swap too.
          there.id = here.id;
          morph(here, there);
          this.#reinit(here);
          swapped += 1;
        }
        if (!swapped) throw new Error('no shared sections in response');

        for (const cards of document.querySelectorAll('vp-version-cards')) {
          cards.dataset.swapView = carried.swapView;
          if (carried.swapSections) cards.dataset.swapSections = carried.swapSections;
          cards.dataset.currentHandle = carried.currentHandle;
        }

        const url = new URL(location.href);
        url.searchParams.set('version', target.pathname.split('/').filter(Boolean).pop() ?? '');
        if (variantId) url.searchParams.set('variant', String(variantId));
        else url.searchParams.delete('variant');
        history.replaceState({}, '', url.toString());
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.warn('[vp-version-cards] page swap failed, navigating', error);
        location.assign(href);
      })
      .finally(() => this.removeAttribute('aria-busy'));
  }

  /** On load, a `?version=` that is not the version already showing is swapped in. */
  #openRequestedVersion() {
    if (!this.dataset.swapView) return;
    const wanted = new URL(location.href).searchParams.get('version');
    if (!wanted || wanted === this.dataset.currentHandle) return;
    const card = [...this.querySelectorAll('.vp-version-card')].find((c) => {
      const href = c.getAttribute('href') || '';
      return href.split('?')[0].split('/').filter(Boolean).pop() === wanted;
    });
    if (!card) return;
    this.#select(card);
    this.#swapFromPage(card.getAttribute('href'), null);
  }

  #swap(href, variantId = null) {
    if (this.dataset.swapView) {
      this.#swapFromPage(href, variantId);
      return;
    }

    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    const target = new URL(href, location.origin);
    // Preserve ?view= so previewing an alternate template survives the swap.
    const view = getViewParameterValue();
    const params = new URLSearchParams();
    params.set('section_id', sectionId);
    if (view) params.set('view', view);
    // Renders the new product already on the matching colour, so the swap lands correctly in
    // one request instead of flashing the default and correcting afterwards.
    if (variantId) params.set('variant', String(variantId));

    this.#abort?.abort();
    this.#abort = new AbortController();
    this.setAttribute('aria-busy', 'true');

    fetch(`${target.pathname}?${params}`, { signal: this.#abort.signal })
      .then((response) => (response.ok ? response.text() : Promise.reject(new Error(String(response.status)))))
      .then((text) => {
        const incoming = new DOMParser().parseFromString(text, 'text/html');
        const current = document.getElementById(`shopify-section-${sectionId}`);
        // A section_id response is the bare section, so it is the document's first element child.
        const next = incoming.getElementById(`shopify-section-${sectionId}`) ?? incoming.body.firstElementChild;
        if (!current || !next) throw new Error('section not found in response');

        morph(current, next);
        this.#reinit(current);

        const url = new URL(location.href);
        url.pathname = target.pathname;
        if (variantId) url.searchParams.set('variant', String(variantId));
        else url.searchParams.delete('variant');
        history.replaceState({}, '', url.toString());

        this.#swapOthers(target, variantId, view);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        // Fall back to a normal navigation rather than leaving the page half-updated.
        console.warn('[vp-version-cards] in-place swap failed, navigating', error);
        location.assign(href);
      })
      .finally(() => this.removeAttribute('aria-busy'));
  }
}

if (!customElements.get('vp-version-cards')) {
  customElements.define('vp-version-cards', VpVersionCards);
}
